"use client";

/**
 * Inviting empty-state strip for /analyze: a two-column band - the
 * 3-step "how it works" list on the left, a large mock preview of the
 * result card on the right (the payoff, shown before a link is pasted).
 * Mirrors CompareLanding so the two flows read as siblings.
 *
 * Desktop: steps left / preview right, vertically centred. Mobile:
 * steps stack first, preview follows full-width.
 *
 * Quiet colours (owner, 2026-10-29: "we overuse the primary colour"):
 * grey icons with no tinted square, grey links; indigo stays for the
 * form's submit button. Bare gray/white classes auto-flip in dark mode.
 */
import { useEffect, useState } from "react";
import { useT } from "@/lib/locale-context";
import { Link2, FileText, BadgeCheck, ArrowRight, Play } from "lucide-react";
import Link from "@/lib/localized-link";
import { getRecentAnalyzed, formatPrice } from "@/lib/api";
import type { RecentAnalyzedItem } from "@/types";

const STEPS = [
  { n: 1, Icon: Link2, key: "analyze.step1" as const },
  { n: 2, Icon: FileText, key: "analyze.step2" as const },
  { n: 3, Icon: BadgeCheck, key: "analyze.step3" as const },
];

export default function AnalyzeLanding() {
  const t = useT();

  // Ready-made examples so "paste a link" never dead-ends on "I don't
  // have a link". Real, recently-analyzed listings (same feed as the
  // homepage carousel) - never a hardcoded URL that can go stale.
  // Clicking one runs the normal /analyze?url= auto-trigger.
  const [samples, setSamples] = useState<RecentAnalyzedItem[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    getRecentAnalyzed()
      .then((d) => { if (!cancelled) setSamples(d.slice(0, 3)); })
      .catch(() => { if (!cancelled) setSamples([]); });
    return () => { cancelled = true; };
  }, []);

  // One slim full-width row of three steps (stacked on mobile) - the
  // old vertical stack of padded cards with decorative numerals was
  // pure chrome around three short captions.
  return (
    <section aria-labelledby="analyze-how">
      <div>
        <div>
          <h2 id="analyze-how" className="text-[11px] text-gray-400 font-semibold mb-2.5">
            {t("analyze.how")}
          </h2>
          <ol className="grid gap-2 sm:grid-cols-3">
            {STEPS.map(({ n, Icon, key }) => (
              <li key={n} className="flex items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
                <Icon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                <p className="text-[13px] font-medium text-gray-700 leading-snug">{t(key)}</p>
              </li>
            ))}
          </ol>
          {samples && samples.length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] font-semibold text-gray-400 mb-2">
                {t("analyze.samples.title")}
              </p>
              <div className="flex flex-wrap gap-2">
                {samples.map((s) => (
                  <Link
                    key={s.id}
                    href={`/analyze?url=${encodeURIComponent(s.listing_url)}`}
                    className="group inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-gray-300 hover:text-gray-900 transition-colors"
                  >
                    <Play className="w-3 h-3 text-gray-400" aria-hidden />
                    {[
                      s.location_name,
                      s.room_count != null ? `${s.room_count} ${t("district.rooms.suffix")}` : null,
                      s.price != null ? `${formatPrice(s.price)} AZN` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {/* Layer-2 pointer: the "is it just ChatGPT?" doubt starts right
              here at the input, so the methodology link lives here too. */}
          <p className="mt-4 text-[13px] text-gray-500">
            {t("analyze.method.lead")}{" "}
            <Link
              href="/methodology"
              className="inline-flex items-center gap-0.5 font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
            >
              {t("analyze.method.link")}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
